import React from 'react'
import { TextField as UITextField } from '@carsys/ui'
import { LocaleContext } from '@cbgms/base/locale/LocaleContext'
import { useMaskedValue } from '@carsys/form/src/utils/useMaskedValue'
import { isInvalid } from './utils'
import { FieldHookConfig, useField } from 'formik'
import { useRegisterFormField } from './TabbedForm/FormTabPanel'

interface CurrencyInputProps<Value>
  extends Omit<React.ComponentProps<typeof UITextField>, 'component' | 'defaultChecked' | 'defaultValue' | 'name' | 'value'>,
    Pick<FieldHookConfig<Value>, 'name' | 'validate'> {
  noNegative?: boolean
}

const toFloat = (input: string) => {
  if (input === '') {
    return 0
  }
  return parseFloat(input?.toString()?.replace(',', '.'))
}

export function CurrencyInput<Value>({ name, validate, noNegative = false, ...props }: CurrencyInputProps<Value>) {
  const [input, meta, helpers] = useField({ name, validate })

  useRegisterFormField(name)

  const { formatter } = React.useContext(LocaleContext)
  const [originalValue, setOriginalValue] = React.useState(input.value)

  const changeHandler = (e: React.FocusEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    let regex = '^'
    if (!noNegative) regex += '-?'
    regex += '(\\d+)?(\\.|\\,)?(\\d{1,2})?$'

    if (new RegExp(regex).test(value) || value === '') {
      setOriginalValue(value)

      helpers.setValue(toFloat(value))
    }
  }

  const handleFocus = () => {
    setOriginalValue(parseFloat(originalValue) ? originalValue : '')
  }

  const [value, events, isEditing] = useMaskedValue(
    input.value ?? 0,
    value => {
      if (!isEditing) {
        setOriginalValue(value)
      }
      const formatted = formatter.format(toFloat(value))
      return formatted
    },
    { onChange: changeHandler, onBlur: input.onBlur, onFocus: () => handleFocus() }
  )

  return (
    <UITextField
      id={input.name}
      error={isInvalid(meta)}
      {...props}
      {...input}
      {...events}
      value={isEditing ? originalValue : value}
      helperText={isInvalid(meta) ? meta.error : props.helperText}
      autoComplete='none'
    />
  )
}
